import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, A11y, FreeMode, Mousewheel } from 'swiper/modules';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import Heading from '@components/ui/Heading';
import IconWrapper from '@components/ui/Icons';

import useObjectVisibility from '@hooks/useObjectVisibility';
import ProductCard from './ProductCard';
import ProductCardAlt from './ProductCardAlt';
import 'swiper/css';
import {
  GA4_EVENT_NAMES,
  trackProductEvent,
  generateProductTrackingData,
} from '@utils/gtm/ga4';
import { setSelectItemCookie } from '@utils/gtm';
import { getVisibleSlideIndexes } from '@utils/swiper';
import { Loader, AlgonomyProduct, AlgonomyPlacement } from './Loader';

import styles from './Recommendations.module.scss';

export { Loader as RecommendationsLoader };
export type { AlgonomyPlacement };

export interface RecommendationsProps {
  placement: AlgonomyPlacement;
  isSmall?: boolean;
  isHome?: boolean;
  isCart?: boolean;
  isPlp?: boolean;
  trackSlideClickCallback?: (data?: {
    placement: AlgonomyPlacement;
    products: (ReturnType<typeof generateProductTrackingData> | null)[];
  }) => void;
  trackSlideViewCallback?: (data?: {
    placement: AlgonomyPlacement;
    products: (ReturnType<typeof generateProductTrackingData> | null)[];
  }) => void;
}

const { SELECT_ITEM, VIEW_ITEM_LIST } = GA4_EVENT_NAMES;

const Recommendations = ({
  placement,
  isSmall,
  isCart,
  isHome,
  isPlp,
  trackSlideClickCallback,
  trackSlideViewCallback,
}: RecommendationsProps) => {
  const [recommendationSwiper, setRecommendationSwiper] =
    useState<SwiperType | null>(null);
  const { t } = useTranslation('common');

  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);
  const [viewedSlides, setViewedSlides] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setFade = (swiper: SwiperType) => {
    setFadeLeft(!swiper.isBeginning);
    setFadeRight(!swiper.isEnd);
  };

  const trackClick = (i: number) => {
    if (!placement?.items) return;
    const trackingData = generateProductTrackingData(
      placement.items[i],
      i,
      placement?.name
    );
    setSelectItemCookie({
      eventName: SELECT_ITEM,
      products: [trackingData],
      itemListName: placement?.name,
    });
    trackSlideClickCallback?.({ products: [trackingData], placement });
  };

  const trackSlideView = useCallback(
    (visibleSlideIndexes: number[] = []) => {
      const untrackedSlideIndexes = visibleSlideIndexes.filter(
        (slide) => viewedSlides?.indexOf(slide) === -1
      );

      const products = untrackedSlideIndexes.map((i) => {
        if (!placement?.items) return null;
        return generateProductTrackingData(
          placement.items[i],
          i,
          placement?.name
        );
      });

      if (untrackedSlideIndexes.length > 0) {
        trackProductEvent({
          eventName: VIEW_ITEM_LIST,
          products,
          itemListName: placement?.name,
        });
        trackSlideViewCallback?.({
          placement,
          products,
        });

        setViewedSlides((currentSlides) => [
          ...currentSlides,
          ...untrackedSlideIndexes,
        ]);
      }
    },
    [placement, trackSlideViewCallback, viewedSlides]
  );
  const [hasInitialTrackingFired, setInitialTrackingFired] = useState(false);
  const isCarouselVisible = useObjectVisibility(containerRef);
  useEffect(() => {
    if (recommendationSwiper && isCarouselVisible && !hasInitialTrackingFired) {
      trackSlideView(getVisibleSlideIndexes(recommendationSwiper));
      setInitialTrackingFired(true);
    }
  }, [
    isCarouselVisible,
    recommendationSwiper,
    trackSlideView,
    hasInitialTrackingFired,
  ]);

  const slides = placement?.items?.map(
    (product: AlgonomyProduct, i: number) => (
      <SwiperSlide key={product.id} className={styles.thumbSlide}>
        {isPlp ? (
          <ProductCardAlt
            displayName={product.displayName}
            displayPrice={product.displayPrice}
            productImage={product.imageURL}
            productUrl={product?.linkURL ?? product?.detailURL}
            salePrice={product.displaySalePrice}
            trackClick={() => trackClick(i)}
          />
        ) : (
          <ProductCard
            displayName={product.displayName}
            displayPrice={product.displayPrice}
            productImage={product.imageURL}
            productUrl={product?.linkURL ?? product?.detailURL}
            rating={product.rating}
            reviews={product.reviews}
            salePrice={product.displaySalePrice}
            trackClick={() => trackClick(i)}
          />
        )}
      </SwiperSlide>
    )
  );

  return (
    <div
      className={cn({
        [styles.homeContainer]: isHome,
        [styles.container]: !isHome,
        [styles.cartContainer]: isCart,
      })}
      ref={containerRef}
    >
      <Heading
        semanticLevel="h2"
        styleLevel="4"
        className={cn({
          [styles.homeHeader]: isHome,
          [styles.header]: !isHome,
        })}
      >
        {placement?.message}
      </Heading>
      <div
        className={cn(styles.swiperWrapper, {
          [styles.swiperWrapperPlp]: isPlp,
        })}
      >
        <div
          aria-hidden
          className={cn(styles.fadeLeft, {
            [styles.fadeLeftHide]: !fadeLeft,
            [styles.fadeLeftSmall]: isSmall,
          })}
        />
        <div
          aria-hidden
          className={cn(styles.fadeRight, {
            [styles.fadeRightHide]: !fadeRight,
            [styles.fadeRightSmall]: isSmall,
          })}
        />
        <Swiper
          modules={[Navigation, A11y, FreeMode, Mousewheel]}
          className={cn(styles.swiper, {
            [styles.plpSwiper]: isPlp,
          })}
          navigation={{
            nextEl: styles.swipeButtNext,
            prevEl: styles.swipeButtPrev,
          }}
          spaceBetween={16}
          slidesPerView={!isPlp ? 2 : 'auto'}
          initialSlide={0}
          onSwiper={setRecommendationSwiper}
          onReachBeginning={setFade}
          onReachEnd={setFade}
          watchSlidesProgress
          threshold={2}
          onInit={(swiper) => {
            setFade(swiper);
          }}
          onSlideChange={(swiper) => {
            trackSlideView(getVisibleSlideIndexes(swiper));
            setFade(swiper);
          }}
          mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
          freeMode={{ enabled: true }}
          breakpoints={
            !isPlp
              ? {
                  897: {
                    slidesPerView: isSmall ? 2 : 4,
                    spaceBetween: isSmall ? 16 : 24,
                  },
                }
              : undefined
          }
        >
          {slides}
          <button
            type="button"
            className={cn(styles.swipeButtPrev, {
              [styles.swipeButtHide]: !fadeLeft,
            })}
            onClick={() => recommendationSwiper?.slidePrev()}
          >
            <span className={styles.screenReaderOnly}>
              {t('carousel.prev')}
            </span>
            <IconWrapper icon="caret-left" width={30} />
          </button>
          <button
            type="button"
            className={cn(styles.swipeButtNext, {
              [styles.swipeButtHide]: !fadeRight,
            })}
            onClick={() => recommendationSwiper?.slideNext()}
          >
            <span className={styles.screenReaderOnly}>
              {t('carousel.next')}
            </span>
            <IconWrapper icon="caret-right" width={30} />
          </button>
        </Swiper>
      </div>
    </div>
  );
};

export default Recommendations;
